import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmMemory } from "@/components/ConfirmMemory";
import { TextCapture, VoiceCapture, PhotoCapture, DocumentCapture, CameraChoice } from "@/components/capture/Captures";
import { PaywallSheet } from "@/components/PaywallSheet";
import { VaultPinDialog } from "@/components/VaultSave";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage, isPaywallError } from "@/lib/api";
import { getVaultToken, saveToVault } from "@/lib/vault";
import { longDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TITLES = { texto: "Nova memória", voz: "Falar", camera: "Câmera", foto: "Fotografar", video: "Vídeo", documento: "Documento" };

export default function NewMemory() {
  const { user } = useAuth();
  const { mode: routeMode } = useParams();
  const [pick, setPick] = useState(null);
  const mode = pick || routeMode;
  const navigate = useNavigate();
  const [step, setStep] = useState("capture");
  const [input, setInput] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [toVault, setToVault] = useState(false);
  const [pendingVault, setPendingVault] = useState(null);

  const saveVault = async (items) => {
    if (!getVaultToken()) return setPendingVault(items);
    setSaving(true);
    try {
      await saveToVault(items);
      toast.success("Guardado na Pasta Segura.");
      navigate("/pasta-segura", { replace: true });
    } catch (e) {
      if (e?.response?.status === 423) setPendingVault(items);
      else if (isPaywallError(e)) setPaywall(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const vaultProps = { toVault, setToVault, onSaveVault: saveVault, saving, isPremium: user?.plan !== "free" };

  const analyze = async (payload) => {
    setInput(payload);
    setStep("analyzing");
    try {
      const { data } = await api.post("/ai/analyze", { text: payload.text || "", file_ids: payload.file_ids || [], source_type: payload.source_type || "text" });
      setAnalysis(data);
      setStep("confirm");
    } catch (e) {
      toast.error(errorMessage(e));
      setStep("capture");
    }
  };

  const onDocument = ({ file, analysis: a }) => {
    setInput({ text: "", file_ids: [file.id], source_type: "document" });
    setAnalysis(a);
    setStep("confirm");
  };

  const save = async (data) => {
    setSaving(true);
    try {
      const body = {
        original_text: input?.text || data.summary, title: data.title, summary: data.summary, category: data.category,
        people: data.people || [], objects: data.objects || [], location: data.location || null, event_date: data.event_date || null,
        value: data.value ?? null, keywords: data.keywords || [], source_type: input?.source_type || "text",
        attachment_ids: input?.file_ids || [], loan: data.loan || null, document: data.document || null,
      };
      const { data: saved } = await api.post("/memories", body);
      toast.success("Pronto. Guardei para você.");
      const loanSuggestion = data.loan?.is_loan && data.loan.direction === "lent" && !data.reminder_suggestion
        ? { title: `Pedir ${data.objects?.[0] || data.title} de volta`, due_date: new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10), question: "Quer definir uma data para lembrar de pedir de volta?" }
        : null;
      const sug = data.reminder_suggestion || loanSuggestion;
      if (sug?.due_date) setSuggestion({ ...sug, memory_id: saved.id });
      else navigate(`/memorias/${saved.id}`, { replace: true });
    } catch (e) {
      if (isPaywallError(e)) setPaywall(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const acceptSuggestion = async () => {
    try {
      await api.post("/reminders", { title: suggestion.title, memory_id: suggestion.memory_id, due_at: new Date(`${suggestion.due_date}T09:00:00`).toISOString(), recurrence: "once" });
      toast.success("Lembrete criado.");
    } catch (e) {
      toast.error(errorMessage(e));
    }
    navigate(`/memorias/${suggestion.memory_id}`, { replace: true });
  };

  return (
    <div className="pb-8">
      <PageHeader title={TITLES[mode] || "Nova memória"} />
      {step === "capture" && mode === "texto" && <TextCapture onAnalyze={analyze} initial={input?.text || ""} vault={vaultProps} />}
      {step === "capture" && mode === "voz" && <VoiceCapture onAnalyze={analyze} onPaywall={setPaywall} vault={vaultProps} />}
      {step === "capture" && mode === "camera" && <CameraChoice onPick={setPick} isPremium={user?.plan !== "free"} />}
      {step === "capture" && mode === "foto" && <PhotoCapture onAnalyze={analyze} onPaywall={setPaywall} vault={vaultProps} />}
      {step === "capture" && mode === "video" && <PhotoCapture media="video" onAnalyze={analyze} onPaywall={setPaywall} vault={vaultProps} />}
      {step === "capture" && mode === "documento" && <DocumentCapture onResult={onDocument} onPaywall={setPaywall} isPremium={user?.plan !== "free"} vault={vaultProps} />}

      {step === "analyzing" && (
        <div className="flex flex-col items-center px-5 pt-16 text-center" data-testid="analyzing-state">
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl" style={{ background: "#05324A" }}>
            <Sparkles size={34} color="#33D9B2" className="animate-pulse" />
          </div>
          <p className="mt-6 text-lg font-semibold">Entendendo o que você quer guardar...</p>
          <p className="ga-muted mt-1 text-sm">Organizando título, categoria e detalhes.</p>
        </div>
      )}

      {step === "confirm" && analysis && <ConfirmMemory analysis={analysis} saving={saving} onSave={save} onBack={() => setStep("capture")} />}

      <Dialog open={!!suggestion} onOpenChange={(o) => !o && navigate(`/memorias/${suggestion?.memory_id}`, { replace: true })}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="reminder-suggestion-dialog">
          <DialogTitle className="text-lg">Quer que eu lembre disso depois?</DialogTitle>
          <DialogDescription className="text-sm">{suggestion?.question || `Deseja ser lembrado em ${suggestion?.due_date ? longDate(suggestion.due_date) : ""}?`}</DialogDescription>
          {suggestion && <p className="ga-surface rounded-2xl p-3 text-sm"><strong>{suggestion.title}</strong><br /><span className="ga-muted">{longDate(suggestion.due_date)}</span></p>}
          <div className="mt-2 flex gap-3">
            <button onClick={() => navigate(`/memorias/${suggestion.memory_id}`, { replace: true })} className="ga-btn-ghost flex-1 uppercase" data-testid="suggestion-decline-button">Agora não</button>
            <button onClick={acceptSuggestion} className="ga-btn-primary flex-1 uppercase" data-testid="suggestion-accept-button">Sim</button>
          </div>
        </DialogContent>
      </Dialog>

      <VaultPinDialog open={!!pendingVault} onClose={() => setPendingVault(null)} onUnlocked={() => { const items = pendingVault; setPendingVault(null); saveVault(items); }} />
      <PaywallSheet open={!!paywall} reason={paywall} onClose={() => setPaywall(null)} />
    </div>
  );
}
